/**
 * TypeSubtypeSelect — a presentational control for a field's durable value type
 * and (when numeric) its display subtype. Shared by the Custom Attributes screen
 * and the mapping-screen type editing, so both choose a type the same way.
 *
 * A type Select offers the four `INFERRED_TYPES`; a subtype Select appears ONLY when
 * the type is numeric, offering the `NUMERIC_SUBTYPES` (both percent variants appear
 * flat). Switching to numeric defaults the subtype to `number`; switching away clears
 * it to null. With a `numericMax` hint, the percent variant `defaultPercentVariant`
 * implies is marked "matches your data" in the dropdown — a recommendation, never an
 * override: every explicit pick is honored verbatim. All labels go
 * through i18n. Pure: it owns no state, it calls `onChange(valueType, valueSubtype)`.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultPercentVariant,
  INFERRED_TYPES,
  NUMERIC_SUBTYPES,
  type InferredType,
  type NumericSubtype,
} from '@repo/shared/common';
import { Group, Select } from '~/components/ui';

export interface TypeSubtypeSelectProps {
  /** The current value type. */
  readonly valueType: InferredType;
  /** The current display subtype (numeric only), or null. */
  readonly valueSubtype: NumericSubtype | null;
  /** Called with the next type + subtype whenever either changes. */
  readonly onChange: (valueType: InferredType, valueSubtype: NumericSubtype | null) => void;
  readonly disabled?: boolean;
  /** Mantine input size for both selects (default `sm`). */
  readonly size?: string;
  /** Base for the two selects' ids/aria, e.g. `idBase="net-balance"`. */
  readonly idBase?: string;
  /**
   * Render a visible "Type" / "Format" label above each select. Off by default so
   * dense callers that supply their own heading (a table column, the retype modal's
   * caption) stay compact; on where the control stands alone without other context
   * (the upload mapping row) so it's clear which dropdown is which.
   */
  readonly withLabels?: boolean;
  /**
   * Smart-percent hint: the field's maximum stored numeric value (from the column
   * analysis or the field registry's stats), or null/omitted when unknown. When set,
   * the percent variant `defaultPercentVariant(numericMax)` implies — a stored
   * max > 1 would read as over 100% under the fraction variant — is marked
   * "matches your data" in the Format dropdown. A visible recommendation only;
   * every explicit pick is honored. Display-only; storage is never touched.
   */
  readonly numericMax?: number | null;
}

export function TypeSubtypeSelect({
  valueType,
  valueSubtype,
  onChange,
  disabled = false,
  size = 'sm',
  idBase,
  withLabels = false,
  numericMax = null,
}: TypeSubtypeSelectProps): ReactNode {
  const { t } = useTranslation();

  const typeOptions = INFERRED_TYPES.map((type) => ({
    value: type,
    label: t(`fieldTypes.${type}`),
  }));
  // With a known stored max, mark the percent variant the data implies (a max > 1
  // would read as over 100% under the fraction variant) — a visible recommendation,
  // never an override: every explicit pick is honored verbatim.
  const impliedPercent = numericMax !== null ? defaultPercentVariant(numericMax) : null;
  const subtypeOptions = NUMERIC_SUBTYPES.map((subtype) => ({
    value: subtype,
    label:
      subtype === impliedPercent
        ? t('numericSubtypes.matchesData', { label: t(`numericSubtypes.${subtype}`) })
        : t(`numericSubtypes.${subtype}`),
  }));

  return (
    <Group gap="sm" align="flex-end" wrap="nowrap">
      <Select
        aria-label={t('fieldTypes.typeLabel')}
        {...(withLabels ? { label: t('fieldTypes.typeLabel') } : {})}
        data={typeOptions}
        value={valueType}
        onChange={(next) => {
          // Mantine narrows `next` to the typed-`data` value union (InferredType).
          if (next === null || next === valueType) return;
          // Switching to numeric seeds a sensible default subtype; switching away
          // clears it (subtype is meaningless for non-numeric types).
          const nextSubtype: NumericSubtype | null = next === 'numeric' ? 'number' : null;
          onChange(next, nextSubtype);
        }}
        allowDeselect={false}
        disabled={disabled}
        size={size}
        comboboxProps={{ withinPortal: true }}
        {...(idBase !== undefined ? { id: `${idBase}-type` } : {})}
      />

      {valueType === 'numeric' ? (
        <Select
          aria-label={t('numericSubtypes.subtypeLabel')}
          {...(withLabels ? { label: t('numericSubtypes.subtypeLabel') } : {})}
          data={subtypeOptions}
          value={valueSubtype ?? 'number'}
          onChange={(next) => {
            // `next` is narrowed to the typed-`data` value union (NumericSubtype).
            if (next === null) return;
            onChange('numeric', next);
          }}
          allowDeselect={false}
          disabled={disabled}
          size={size}
          comboboxProps={{ withinPortal: true }}
          {...(idBase !== undefined ? { id: `${idBase}-subtype` } : {})}
        />
      ) : null}
    </Group>
  );
}
